import { useEffect, useState } from 'react';
import { FC } from 'react';
import {
  APIProps,
  OfferDataForPublisherEvent,
  OfferDataForPublisherEventV2,
  IUseApi,
} from 'utils/types';
import { BootResponse, OfferData } from 'utils/types';
import {
  EEventsType,
  ELocalStorageKeys,
  EPublisherWebhookOfferEventType,
  EPublisherWebhookOfferEventTypeV2,
  EQueryParams,
  EStorePhase,
} from 'utils/enums';
import { EPublisherWebhookEventType } from 'utils/enums';
import { AccountApproval } from 'components/AccountApproval';
import { Dict } from 'mixpanel-browser';
import { Modal } from '@mui/material';

const PUBLISHER_EVENTS_V2_TYPE = 'offers';

interface IAccountApprovalModalProps {
  selectedOffer:
    | OfferData
    | OfferDataForPublisherEvent
    | OfferDataForPublisherEventV2
    | string;
  webStorageUtil: {
    get: (key: string) => any;
    set: (key: string, value: string) => void;
    remove: (key: string) => void;
  };
  useApi: (props: APIProps) => IUseApi;
  t: any;
  sendCustomEvent: (
    eventType: string,
    options: Dict,
    eventPhase?: EStorePhase,
  ) => void;
  loginState: {
    logout: () => void;
  };
  navigate: any;
}

export const AccountApprovalModal: FC<IAccountApprovalModalProps> = ({
  selectedOffer,
  webStorageUtil,
  useApi,
  t,
  sendCustomEvent,
  loginState,
  navigate,
}) => {
  const {
    sendPublisherWebhookEvent,
    sendPublisherWebhookEventV2,
    getPublisherMeta,
  } = useApi({});

  const publisherMetaData = getPublisherMeta.data as BootResponse;
  const [isValidated, setIsValidated] = useState<boolean>(false);
  const { logout } = loginState;

  useEffect(() => {
    if (isValidated) return;
    sendCustomEvent(EEventsType.DEEPLINK_ACCOUNT_APPROVAL_DISPLAYED, {
      approved_account: true,
      source: 'deeplink',
      source_type: 'dynamo',
    });
    sendPublisherWebhookEvent?.mutate({
      eventType: EPublisherWebhookEventType.OFFER,
      data: {
        eventName: EPublisherWebhookOfferEventType.APPROVAL_REQUEST_DISPLAYED,
      },
    });

    sendPublisherWebhookEventV2?.mutate({
      eventType: PUBLISHER_EVENTS_V2_TYPE,
      data: {
        eventName:
          EPublisherWebhookOfferEventTypeV2.WEBSTORE_APPROVAL_REQUEST_DISPLAYED,
      },
    });
  }, [isValidated]);

  const setSkipParam = () => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.append(EQueryParams.SKIP_APPROVAL, 'true');

    navigate(`${window.location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleAccountApproval = () => {
    setIsValidated(true);

    setSkipParam();

    webStorageUtil.set(
      ELocalStorageKeys.DEEPLINK_APPROVED_SESSION_TOKEN,
      webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN),
    );

    sendCustomEvent(EEventsType.DEEPLINK_ACCOUNT_APPROVAL_APPROVE, {
      approved_account: true,
      offerId: selectedOffer,
      source: 'deeplink',
      source_type: 'dynamo',
    });

    sendPublisherWebhookEvent?.mutate({
      eventType: EPublisherWebhookEventType.OFFER,
      data: {
        eventName: EPublisherWebhookOfferEventType.APPROVAL_REQUEST_CLOSED,
        reason: 'approve',
      },
    });

    sendPublisherWebhookEventV2?.mutate({
      eventType: PUBLISHER_EVENTS_V2_TYPE,
      data: {
        eventName:
          EPublisherWebhookOfferEventTypeV2.WEBSTORE_APPROVAL_REQUEST_CLOSED,
        reason: 'approve',
      },
    });
  };

  const handleAccountDisapproval = () => {
    sendCustomEvent(EEventsType.DEEPLINK_ACCOUNT_APPROVAL_APPROVE, {
      approved_account: false,
      offerId: selectedOffer,
      source: 'deeplink',
      source_type: 'dynamo',
    });

    logout();

    sendPublisherWebhookEvent?.mutate({
      eventType: EPublisherWebhookEventType.OFFER,
      data: {
        eventName: EPublisherWebhookOfferEventType.APPROVAL_REQUEST_CLOSED,
        reason: 'not_me',
      },
    });

    sendPublisherWebhookEventV2?.mutate({
      eventType: PUBLISHER_EVENTS_V2_TYPE,
      data: {
        eventName:
          EPublisherWebhookOfferEventTypeV2.WEBSTORE_APPROVAL_REQUEST_CLOSED,
        reason: 'not_me',
      },
    });
  };

  return (
    <Modal open={!isValidated} className="account-approval-modal">
      <AccountApproval
        onApproval={handleAccountApproval}
        onDisapproval={handleAccountDisapproval}
        design={publisherMetaData.storeTheme?.login?.accountApprovalModal}
        privacyPolicyUrl={
          publisherMetaData.storeTheme.general?.footer.privacyPolicyUrl
        }
        termsAndConditionsUrl={
          publisherMetaData.storeTheme.general?.footer.termsAndConditionsUrl
        }
        translations={{
          title: t('accountApprovalModal.title'),
          approve: t('accountApprovalModal.approve'),
          disapprove: t('accountApprovalModal.disapprove'),
          byClicking: t('accountApprovalModal.byClicking'),
          and: t('accountApprovalModal.and'),
          privacyPolicy: t('accountApprovalModal.privacyPolicy'),
          termsAndConditions: t('accountApprovalModal.termsAndConditions'),
        }}
        playerName={
          webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)?.playerName
        }
        playerImage={
          webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)?.profileImageUrl
        }
      />
    </Modal>
  );
};
